import React, { useCallback } from 'react';
import { Pill } from '@lifeomic/chroma-react/components/Pill';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Edit2, Eye, EyeOff, Trash2, Plant } from '@lifeomic/chromicons';

import { defineMessages, useIntl } from 'react-intl';
import clsx from 'clsx';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import {
  ACTION_PLAN_CATEGORIES,
  ActionPlanActivity,
  ACTION_PLAN_CATEGORIES_ICONS,
} from './action-plan';
import { SYSTEM_ACTION_PLAN_NOTES } from '../../../../util/fhir';
import { isAiGenerated } from '../ExecutiveSummary/utils';
import { ContainerQuery } from 'react-container-query';
import type { ViewMode } from '../AITools/Attachments/InlineAttachment';

const useStyles = makeStyles(theme => ({
  actionItem: {
    background: theme.palette.common.white,
    width: '100%',
    height: 'fit-content',
    position: 'relative',
    padding: theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'row',
    '& + $actionItem': {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    '&:has(+ $hide)': {
      borderBottomLeftRadius: theme.pxToRem(10),
      borderBottomRightRadius: theme.pxToRem(10),
    },
  },
  actionsItemNarrow: {
    paddingRight: theme.pxToRem(72),
  },
  title: {
    color: theme.palette.text.dark,
    flex: 1,
    flexBasis: '100%',
    fontWeight: 'bold',
    gap: theme.spacing(1),
    maxWidth: '65ch',
    lineHeight: 1.35,
  },
  description: {
    color: theme.palette.text.hint,
    fontSize: theme.typography.subtitle1.fontSize,
    flexBasis: '100%',
    maxWidth: '65ch',
    marginTop: theme.spacing(-1.25),
    lineHeight: 1.5,
    // pre-wrap keeps newlines in the text so that the preview is more readable and
    // matches what the clinician writes and member sees in-app
    whiteSpace: 'pre-wrap',
  },
  reasonsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    maxWidth: '65ch',
  },
  actionsContainer: {
    alignItems: 'flex-start',
    display: 'flex',
    justifyContent: 'flex-end',
    marginLeft: 'auto',
    marginRight: theme.spacing(-0.5),
    marginTop: theme.spacing(-0.5),
    minHeight: theme.pxToRem(26),
    '& > div > button': {
      padding: theme.spacing(0.25, 0.75),
    },
  },
  actionsContainerNarrow: {
    background: theme.hexToRgba(theme.palette.graphite[900], 0.05),
    height: `calc(100%)`,
    marginRight: 0,
    marginTop: 0,
    position: 'absolute',
    flex: 0,
    right: 0,
    top: 0,
    paddingTop: theme.pxToRem(20),
    '& $actionsFlex': {
      flexDirection: 'column',
      padding: theme.spacing(0, 1.5),
      gap: theme.spacing(1.25),
    },
    '& $notVisiblePill': {
      display: 'none',
    },
  },
  actionsFlex: {
    minHeight: theme.pxToRem(26),
    flexWrap: 'wrap',
  },
  notVisible: {
    transition: 'opacity 0.2s ease-out',
    opacity: 0.5,
  },
  notVisiblePill: {
    marginRight: theme.spacing(0.25),
    whiteSpace: 'nowrap',
  },
  aiPill: {
    border: 'none',
    height: theme.pxToRem(18),
    padding: theme.spacing(0.25, 1),
    position: 'relative',
    top: theme.pxToRem(-2),
    left: theme.pxToRem(10),
    whiteSpace: 'nowrap',
  },
  descriptionAndActions: {
    flexWrap: 'wrap',
  },
  activityIconRoot: {
    backgroundColor: theme.palette.darkGraphite[50],
    maxHeight: theme.pxToRem(42),
    maxWidth: theme.pxToRem(42),
    minHeight: theme.pxToRem(42),
    minWidth: theme.pxToRem(42),
    borderRadius: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  activityIcon: {
    color: theme.palette.blue[900],
  },
  reasonPill: {
    whiteSpace: 'nowrap',
  },
  hide: {
    display: 'none',
  },
}));

const messages = defineMessages({
  notVisible: {
    id: 'actionPlan.actionActivity.notVisible',
    defaultMessage: 'Not Visible',
  },
  notVisibleTooltip: {
    id: 'actionPlan.actionActivity.notVisibleTooltip',
    defaultMessage: 'This action item is not visible to the member',
  },
  aiSuggestion: {
    id: 'actionPlan.actionActivity.aiSuggestion',
    defaultMessage: 'AI',
  },
  aiSuggestionTooltip: {
    id: 'actionPlan.actionActivity.aiSuggestionTooltip',
    defaultMessage: 'This action item was suggested by AI',
  },
  editItem: {
    id: 'actionPlan.actionActivity.edit',
    defaultMessage: 'Edit',
  },
  toggleItemVisibility: {
    id: 'actionPlan.actionActivity.toggleVisibility',
    defaultMessage: 'Toggle Visibility',
  },
  deleteItem: {
    id: 'actionPlan.actionActivity.delete',
    defaultMessage: 'Delete',
  },
});

const query = {
  isNarrow: {
    maxWidth: 950,
  },
};

type ActionActivityProps = {
  activity: fhir.CarePlanActivity;
  onDelete: (activity: fhir.CarePlanActivity) => void;
  onEdit: (activity: fhir.CarePlanActivity) => void;
  toggleVisibility: (activity: fhir.CarePlanActivity) => void;
  viewMode?: ViewMode;
};

type ActivityIconProps = {
  activity: fhir.CarePlanActivity;
  isVisible: boolean;
};

const ActivityIcon = React.memo(
  ({ activity, isVisible }: ActivityIconProps) => {
    const classes = useStyles({});
    const category = new ActionPlanActivity(
      activity,
    ).getCategory() as (typeof ACTION_PLAN_CATEGORIES)[number];
    const Icon = ACTION_PLAN_CATEGORIES_ICONS[category] || Plant;

    return (
      <div
        className={clsx(
          classes.activityIconRoot,
          !isVisible ? classes.notVisible : undefined,
        )}
      >
        <Icon className={classes.activityIcon} />
      </div>
    );
  },
);

const _ActionActivity = ({
  activity,
  onDelete,
  onEdit,
  toggleVisibility,
  viewMode,
}: ActionActivityProps) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const handleDelete = useCallback(() => {
    onDelete(activity);
  }, [onDelete, activity]);

  const handleEdit = useCallback(() => {
    onEdit(activity);
  }, [onEdit, activity]);

  const handleToggleVisibility = useCallback(() => {
    toggleVisibility(activity);
  }, [toggleVisibility, activity]);

  if (!activity.detail) return null; // Should not get in this state, but making TS happy

  const isVisible = activity.detail.status === 'unknown';
  const isAiSuggested = isAiGenerated(activity.detail);

  return (
    <>
      <ContainerQuery query={query}>
        {params => (
          <Box
            className={clsx(
              classes.actionItem,
              !isVisible &&
                (viewMode === 'presentation-expanded' ||
                  viewMode === 'presentation-compact') &&
                classes.hide,
              params.isNarrow && classes.actionsItemNarrow,
            )}
          >
            <Box direction="column" gap={2}>
              <ActivityIcon activity={activity} isVisible={isVisible} />
            </Box>
            <Box
              gap={2}
              marginLeft={2}
              className={classes.descriptionAndActions}
              fullWidth
            >
              <Text
                className={clsx(
                  classes.title,
                  !isVisible && classes.notVisible,
                )}
              >
                <span>{activity.detail?.description}</span>
                {isAiSuggested &&
                viewMode !== 'presentation-expanded' &&
                viewMode !== 'presentation-compact' ? (
                  <Tooltip title={formatMessage(messages.aiSuggestionTooltip)}>
                    <Pill
                      label={formatMessage(messages.aiSuggestion)}
                      variant="highlight"
                      className={classes.aiPill}
                    />
                  </Tooltip>
                ) : null}
              </Text>
              {viewMode !== 'presentation-expanded' &&
                viewMode !== 'presentation-compact' && (
                  <Box
                    className={clsx(
                      classes.actionsContainer,
                      params.isNarrow && classes.actionsContainerNarrow,
                    )}
                    gap={2}
                  >
                    <Box
                      className={classes.actionsFlex}
                      align="center"
                      justify="flex-end"
                    >
                      {isVisible ? (
                        <Tooltip
                          title={formatMessage(messages.toggleItemVisibility)}
                        >
                          <IconButton
                            aria-label={formatMessage(
                              messages.toggleItemVisibility,
                            )}
                            icon={Eye}
                            size={0}
                            onClick={handleToggleVisibility}
                          />
                        </Tooltip>
                      ) : (
                        <>
                          <Tooltip
                            title={formatMessage(messages.notVisibleTooltip)}
                          >
                            <Pill
                              label={formatMessage(messages.notVisible)}
                              className={classes.notVisiblePill}
                            />
                          </Tooltip>
                          <Tooltip
                            title={formatMessage(messages.toggleItemVisibility)}
                          >
                            <IconButton
                              aria-label={formatMessage(
                                messages.toggleItemVisibility,
                              )}
                              icon={EyeOff}
                              size={0}
                              onClick={handleToggleVisibility}
                            />
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title={formatMessage(messages.editItem)}>
                        <IconButton
                          aria-label={formatMessage(messages.editItem)}
                          icon={Edit2}
                          size={0}
                          onClick={handleEdit}
                        />
                      </Tooltip>
                      <Tooltip title={formatMessage(messages.deleteItem)}>
                        <IconButton
                          aria-label={formatMessage(messages.deleteItem)}
                          icon={Trash2}
                          size={0}
                          onClick={handleDelete}
                        />
                      </Tooltip>
                    </Box>
                  </Box>
                )}
              {activity.detail?.extension?.map(
                (ext, index) =>
                  ext.url === SYSTEM_ACTION_PLAN_NOTES && (
                    <Text
                      key={index}
                      className={clsx(
                        classes.description,
                        !isVisible && classes.notVisible,
                      )}
                    >
                      {ext.valueString}
                    </Text>
                  ),
              )}
              <Box
                className={clsx(
                  classes.reasonsContainer,
                  !isVisible && classes.notVisible,
                )}
                gap={1}
              >
                {activity.detail?.reasonReference?.map((reason, index) => (
                  <Pill
                    className={classes.reasonPill}
                    key={index}
                    color="primary"
                  >
                    {reason.display}
                  </Pill>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </ContainerQuery>
    </>
  );
};

export const ActionActivity = React.memo(_ActionActivity);
